'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { useAuth } from '@/contexts/AuthContext';

interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const { user, userProfile, signOut } = useAuth();
  const router = useRouter();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  // Prevent hydration mismatch by ensuring component only renders on client
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const navigationItems = [
  { label: 'Home', href: '/homepage', icon: 'HomeIcon' },
  { label: 'Courses', href: '/course-catalog', icon: 'AcademicCapIcon' },
  { label: 'Dashboard', href: '/student-dashboard', icon: 'ChartBarIcon' },
  { label: 'Live Class', href: '/live-classroom', icon: 'VideoCameraIcon' }];


  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const getInitials = (name: string) => {
    return name.
    split(' ').
    map((part) => part.charAt(0)).
    join('').
    toUpperCase().
    slice(0, 2);
  };

  const handleLogout = () => {
    setShowUserMenu(false);
    setIsMobileMenuOpen(false);
    signOut().then(() => {
      router.push('/auth/signin');
    });
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 bg-white shadow-md ${className}`} suppressHydrationWarning>
      <div className="w-full">
        <div className="flex items-center justify-between h-16 px-4 lg:px-8">
          {/* Logo */}
          <Link href="/homepage" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
            <AppImage
              src="/assets/images/WeSkill_Logo-1768646176551.png"
              alt="WeSkill Academy Logo"
              width={96}
              height={32}
              style={{ height: 'auto', width: 'auto', maxHeight: '32px' }}
              className="h-8 w-auto object-contain" />

          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems.map((item) =>
            <Link
              key={item.href}
              href={item.href}
              className="flex items-center space-x-2 px-4 py-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors duration-300">

                <Icon name={item.icon as any} size={20} variant="outline" />
                <span className="font-cta font-medium">{item.label}</span>
              </Link>
            )}
          </nav>

          {/* Desktop User Menu */}
          <div className="hidden lg:flex items-center space-x-4">
            {isMounted && user && userProfile ?
            <div className="relative">
                <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-3 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors">

                  <div className="w-8 h-8 rounded-full bg-brand-conversion flex items-center justify-center text-white text-sm font-bold">
                    {getInitials(userProfile.full_name)}
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-900">Welcome, {userProfile.full_name.split(' ')[0]}</p>
                    <p className="text-xs text-gray-600 capitalize">{userProfile.role}</p>
                  </div>
                  <Icon name="ChevronDownIcon" size={16} className="text-gray-600" />
                </button>

                {showUserMenu &&
              <>
                    <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowUserMenu(false)} />

                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                      <div className="py-2">
                        <Link
                      href="/user-profile-page"
                      onClick={() => setShowUserMenu(false)}
                      className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors">

                          <Icon name="UserCircleIcon" size={18} />
                          <span className="text-sm">My Profile</span>
                        </Link>
                        <Link
                      href="/student-dashboard"
                      onClick={() => setShowUserMenu(false)}
                      className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors">

                          <Icon name="AcademicCapIcon" size={18} />
                          <span className="text-sm">My Courses</span>
                        </Link>
                        <div className="border-t border-gray-200 my-2" />
                        <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 px-4 py-2 text-red-600 hover:bg-gray-100 transition-colors w-full">

                          <Icon name="ArrowRightOnRectangleIcon" size={18} />
                          <span className="text-sm">Sign Out</span>
                        </button>
                      </div>
                    </div>
                  </>
              }
              </div> :
            isMounted ?
            <>
                <Link
                href="/auth/signin"
                className="px-5 py-2 text-gray-700 font-cta font-medium border border-gray-300 rounded-md hover:bg-gray-100 transition-colors">

                  Sign In
                </Link>
                <Link
                href="/auth/signup"
                className="px-6 py-2 bg-brand-conversion font-cta font-semibold rounded-md hover:bg-brand-conversion/90 transition-all duration-300 shadow-button text-[rgba(61,36,36,1)]">

                  Start Learning
                </Link>
              </> :

            <div className="w-[200px] h-10" />
            }
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 rounded-md hover:bg-gray-100 transition-colors text-gray-700"
            aria-label="Toggle mobile menu">

            <Icon name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={24} variant="outline" />
          </button>
        </div>

        {/* Mobile Menu */}
        {isMounted && isMobileMenuOpen &&
        <div className="lg:hidden bg-white border-t border-gray-200">
            <nav className="flex flex-col py-4 px-4 space-y-2">
              {user && userProfile &&
            <div className="flex items-center space-x-3 px-4 py-3 bg-gray-100 rounded-md mb-2">
                  <div className="w-10 h-10 rounded-full bg-brand-conversion flex items-center justify-center text-white font-bold">
                    {getInitials(userProfile.full_name)}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Welcome, {userProfile.full_name.split(' ')[0]}</p>
                    <p className="text-xs text-gray-600 capitalize">{userProfile.role}</p>
                  </div>
                </div>
            }

              {navigationItems.map((item) =>
            <Link
              key={item.href}
              href={item.href}
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center space-x-3 px-4 py-3 rounded-md text-gray-700 hover:bg-gray-100 transition-colors duration-300">

                  <Icon name={item.icon as any} size={20} variant="outline" />
                  <span className="font-cta font-medium">{item.label}</span>
                </Link>
            )}

              <div className="pt-4 space-y-2 border-t border-gray-200">
                {user && userProfile ?
              <>
                    <Link
                  href="/user-profile-page"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-md transition-colors">

                      <Icon name="UserCircleIcon" size={20} />
                      <span className="font-cta font-medium">My Profile</span>
                    </Link>
                    <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-gray-100 rounded-md transition-colors w-full">

                      <Icon name="ArrowRightOnRectangleIcon" size={20} />
                      <span className="font-cta font-medium">Sign Out</span>
                    </button>
                  </> :

              <>
                    <Link
                  href="/auth/signin"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block w-full px-4 py-3 text-center text-gray-700 font-cta font-medium border border-gray-300 rounded-md hover:bg-gray-100 transition-colors duration-300">

                      Sign In
                    </Link>
                    <Link
                  href="/auth/signup"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block w-full px-4 py-3 text-center bg-brand-conversion text-white font-cta font-semibold rounded-md hover:bg-brand-conversion/90 transition-all duration-300">

                      Start Learning
                    </Link>
                  </>
              }
              </div>
            </nav>
          </div>
        }
      </div>
    </header>);

};

export default Header;